import { Link } from "@tanstack/react-router";
import { Sparkles, ArrowRight, Flame, Zap } from "lucide-react";
import type { ToolMeta } from "@/lib/tools-registry";

interface ToolCardProps {
  tool: ToolMeta;
  variant?: "default" | "featured" | "compact";
  badge?: string;
}

export function ToolCard({ tool, variant = "default", badge }: ToolCardProps) {
  const Icon = tool.icon;

  const isPopular = tool.popular || badge === "Popular";
  const isFeatured = tool.featured || variant === "featured";
  const isNew = tool.isNew || badge === "New";
  const activeBadge = badge || (isPopular ? "Popular" : isFeatured ? "Featured" : isNew ? "New" : null);

  const categoryStyles = {
    finance: {
      iconBg: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 group-hover:bg-emerald-600 group-hover:text-white",
      topBorder: "border-t-2 border-t-emerald-500",
      accentGlow: "group-hover:shadow-emerald-500/10",
      badgeColor: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30",
    },
    students: {
      iconBg: "bg-indigo-500/15 text-indigo-600 dark:text-indigo-400 group-hover:bg-indigo-600 group-hover:text-white",
      topBorder: "border-t-2 border-t-indigo-500",
      accentGlow: "group-hover:shadow-indigo-500/10",
      badgeColor: "bg-indigo-500/15 text-indigo-700 dark:text-indigo-300 border-indigo-500/30",
    },
    daily: {
      iconBg: "bg-amber-500/15 text-amber-600 dark:text-amber-400 group-hover:bg-amber-500 group-hover:text-white",
      topBorder: "border-t-2 border-t-amber-500",
      accentGlow: "group-hover:shadow-amber-500/10",
      badgeColor: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30",
    },
  }[tool.category] || {
    iconBg: "bg-primary-soft text-primary group-hover:bg-primary group-hover:text-primary-foreground",
    topBorder: "border-t-2 border-t-primary",
    accentGlow: "group-hover:shadow-primary/10",
    badgeColor: "bg-primary/15 text-primary border-primary/30",
  };

  return (
    <Link
      to={`/${tool.slug}` as any}
      className={`tool-card group relative flex flex-col justify-between h-full min-h-[210px] rounded-2xl border border-border/80 bg-card/90 p-5 shadow-sm transition-all duration-300 ease-out hover:-translate-y-1.5 hover:shadow-xl ${categoryStyles.accentGlow} hover:border-primary/50 active-click overflow-hidden ${isPopular || isFeatured ? categoryStyles.topBorder : ""}`}
    >
      {/* Subtle background glow wash on hover */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />

      <div>
        {/* Header with icon and visual category badge */}
        <div className="flex items-center justify-between gap-2 mb-3">
          <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${categoryStyles.iconBg} transition-all duration-300 shadow-sm shrink-0 group-hover:scale-110 group-hover:rotate-3`}>
            {typeof Icon === "string" ? (
              <span className="text-xl leading-none">{Icon}</span>
            ) : typeof Icon === "function" || (typeof Icon === "object" && Icon !== null) ? (
              <Icon className="h-5 w-5" />
            ) : (
              <Sparkles className="h-5 w-5" />
            )}
          </span>

          <div className="flex items-center gap-1.5 shrink-0">
            {activeBadge && (
              <span
                className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-extrabold uppercase tracking-wider border shadow-sm transition duration-300 group-hover:scale-105 ${
                  activeBadge === "Popular"
                    ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30"
                    : activeBadge === "Featured"
                    ? "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30"
                    : "bg-purple-500/15 text-purple-700 dark:text-purple-300 border-purple-500/30"
                }`}
              >
                {activeBadge === "Popular" ? (
                  <Sparkles className="h-2.5 w-2.5 animate-pulse text-emerald-500" />
                ) : activeBadge === "Featured" ? (
                  <Flame className="h-2.5 w-2.5 text-amber-500" />
                ) : (
                  <Zap className="h-2.5 w-2.5 text-purple-500" />
                )}
                {activeBadge}
              </span>
            )}
          </div>
        </div>

        {/* Tool Title */}
        <h3 className="font-extrabold text-foreground text-base leading-snug line-clamp-2 mb-1.5 group-hover:text-primary transition-colors duration-200">
          {tool.name.split("|")[0].trim()}
        </h3>

        {/* Description */}
        <p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed mb-4">
          {tool.short}
        </p>
      </div>

      {/* Card Footer: Category label + Primary-colored CTA button */}
      <div className="pt-3 border-t border-border/50 flex items-center justify-between gap-2 mt-auto">
        <span className="text-[11px] font-medium text-muted-foreground/80">
          Free Browser Tool
        </span>

        <span className="inline-flex items-center gap-1.5 text-xs font-bold text-primary group-hover:text-primary-foreground group-hover:bg-primary group-hover:px-2.5 group-hover:py-1 group-hover:rounded-lg transition-all duration-300 shadow-sm">
          Use Tool
          <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform duration-300" />
        </span>
      </div>
    </Link>
  );
}
